import React, { useMemo } from "react";
import {
  Box,
  Typography,
  FormGroup,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Button,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import Loading from "../Shared/Loading";
import DataComponentError from "../Shared/DataComponentError";
import ConnectionSummaryCard from "./ConnectionSummaryCard";
import {
  getBillConfigsQuery,
  getSiteIntegrationsQuery,
  getArcadiaCalendarDetailQuery,
  getArcadiaCalendarDatesQuery,
  getTariffsPropertiesQuery,
} from "../../lib/Queries";

const SiteInfo = (props) => {
  const {
    styles,
    currentSite,
    form,
    lists,
    getCountries,
    getStatus,
    getContractTypes,
    getDealStructures,
    getbillSavingsProviders,
    getServicesProviders,
    isLoading,
    error,
    onNavigateToIntegrations,
  } = props;

  const v = form?.values || {};
  const s = form?.set || {};
  const l = lists || {};

  const { data: billConfigs = [], isLoading: billConfigsLoading } = useQuery({
    ...getBillConfigsQuery(currentSite?.uuid),
    enabled: !!currentSite?.uuid,
  });

  const { data: siteIntegrations = [] } = useQuery({
    ...getSiteIntegrationsQuery(currentSite?.uuid),
    enabled: !!currentSite?.uuid,
  });

  const preConfig = Array.isArray(billConfigs)
    ? billConfigs.find((c) => c.bill_type === "pre_microgrid" && c.active)
    : null;
  const postConfig = Array.isArray(billConfigs)
    ? billConfigs.find((c) => c.bill_type === "post_microgrid" && c.active)
    : null;

  const isEmptyTariffState = !preConfig && !postConfig;

  const { data: preTariffPropertiesData } = useQuery({
    ...getTariffsPropertiesQuery(String(preConfig?.master_tariff_id || "")),
  });

  const { data: postTariffPropertiesData } = useQuery({
    ...getTariffsPropertiesQuery(String(postConfig?.master_tariff_id || "")),
  });

  const preDisplayNameMap = useMemo(() => {
    const results = Array.isArray(preTariffPropertiesData?.results) ? preTariffPropertiesData.results : [];
    return results.reduce((acc, p) => {
      if (p?.keyName) acc[p.keyName] = p.displayName || p.keyName;
      return acc;
    }, {});
  }, [preTariffPropertiesData]);

  const postDisplayNameMap = useMemo(() => {
    const results = Array.isArray(postTariffPropertiesData?.results) ? postTariffPropertiesData.results : [];
    return results.reduce((acc, p) => {
      if (p?.keyName) acc[p.keyName] = p.displayName || p.keyName;
      return acc;
    }, {});
  }, [postTariffPropertiesData]);

  const preOverridesWithDisplayNames = useMemo(() => {
    const overrides = Array.isArray(preConfig?.tariff_properties) ? preConfig.tariff_properties : null;
    if (!overrides) return null;
    return overrides.map((prop) => ({
      ...prop,
      displayName: preDisplayNameMap[prop.key_name] || prop.key_name,
    }));
  }, [preConfig, preDisplayNameMap]);

  const postOverridesWithDisplayNames = useMemo(() => {
    const overrides = Array.isArray(postConfig?.tariff_properties) ? postConfig.tariff_properties : null;
    if (!overrides) return null;
    return overrides.map((prop) => ({
      ...prop,
      displayName: postDisplayNameMap[prop.key_name] || prop.key_name,
    }));
  }, [postConfig, postDisplayNameMap]);

  const arcadiaIntegration = useMemo(
    () =>
      Array.isArray(siteIntegrations)
        ? siteIntegrations.find((i) => i.provider === "arcadia")
        : null,
    [siteIntegrations],
  );

  const calendarId = arcadiaIntegration?.config?.calendar_id || null;
  const calendarName = arcadiaIntegration?.config?.calendar_name || null;

  const { data: calendarDetailData } = useQuery({
    ...getArcadiaCalendarDetailQuery(Number(calendarId)),
    enabled: !!calendarId,
  });

  const fromDateTime = useMemo(() => dayjs().subtract(45, "day").toISOString(), []);
  const toDateTime = useMemo(() => dayjs().add(10, "day").toISOString(), []);

  const { data: calendarDatesData } = useQuery({
    ...getArcadiaCalendarDatesQuery(Number(calendarId), fromDateTime, toDateTime),
    enabled: !!calendarId,
  });

  const periodEndDates = useMemo(() => {
    const dates = calendarDatesData?.results || [];
    return dates
      .filter((d) => d.startDateTime)
      .map((d) => dayjs(d.startDateTime).format("MMM D"))
      .join(", ");
  }, [calendarDatesData]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={currentSite?.uuid ? styles.settingsPanel : { ...styles.settingsNewPanel }}
    >
      <Typography variant="h6" mb="1.5rem">
        Info
      </Typography>
      <FormGroup fullWidth variant="outlined" margin="normal">
        <Box mb="24px">
          <TextField
            id="name"
            name="name"
            label="Site Name"
            size="small"
            value={v.name}
            disabled={!!currentSite?.uuid}
            variant="outlined"
            onChange={(ev) => {
              s.setName(ev.target.value);
            }}
            onBlur={(ev) => {
              s.setName(ev.target.value.trim());
            }}
            sx={{ width: "24.5rem", fontSize: 16, fontWeight: 400 }}
            InputProps={{
              sx: { fontSize: 16, fontWeight: 400 },
            }}
          />
        </Box>
        <Box>
          <Box display="flex" mb="1.5rem" alignItems="center">
            <TextField
              id="address"
              name="address"
              label="Site Address"
              required
              value={v.address1}
              size="small"
              variant="outlined"
              onChange={(ev) => {
                s.setAddress1(ev.target.value);
              }}
              onBlur={(ev) => {
                s.setAddress1(ev.target.value.trim());
              }}
              sx={{
                width: "24.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
              }}
              InputProps={{
                sx: { fontSize: 16, fontWeight: 400 },
              }}
            />
            <TextField
              id="city"
              name="city"
              label="City"
              required
              value={v.city}
              size="small"
              variant="outlined"
              onChange={(ev) => {
                s.setCity(ev.target.value);
              }}
              onBlur={(ev) => {
                s.setCity(ev.target.value.trim());
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
              }}
              InputProps={{
                sx: { fontSize: 16, fontWeight: 400 },
              }}
            />
            <FormControl sx={{ width: "5rem", marginRight: "1.5rem" }}>
              <Autocomplete
                id="usState"
                name="usState"
                options={l.statesOptions}
                size="small"
                getOptionLabel={(option) => option.label}
                value={l.statesOptions?.find((option) => option.value === v.usState) || null}
                onChange={(event, newValue) => {
                  s.setUsState(newValue ? newValue.value : "");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="State" required variant="outlined" />
                )}
                key={(option) => option.value}
                disableClearable
              />
            </FormControl>
            <TextField
              type="number"
              id="zip"
              name="zip"
              label="ZIP"
              required
              value={v.zip}
              size="small"
              variant="outlined"
              onChange={(ev) => {
                s.setZip(ev.target.value);
              }}
              sx={{
                width: "5rem",
                fontSize: 16,
                fontWeight: 400,
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
              InputProps={{
                sx: { fontSize: 16, fontWeight: 400 },
              }}
            />
          </Box>

          <Box display="flex" mb="1.5rem" alignItems="center">
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <InputLabel size="small" htmlFor="country" required>
                Country
              </InputLabel>
              <Select
                id="country"
                name="country"
                label="Country"
                disabled
                required
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                }}
                size="small"
                value={v.country}
                variant="outlined"
                onChange={(ev) => {
                  s.setCountry(ev.target.value);
                }}
              >
                {getCountries()}
              </Select>
            </FormControl>
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <Autocomplete
                id="timezone"
                name="timezone"
                options={l.timezonesOptions}
                getOptionLabel={(option) => option.label}
                value={l.timezonesOptions?.find((option) => option.value === v.timezone) || null}
                onChange={(event, newValue) => {
                  s.setTimezone(newValue ? newValue.value : "");
                }}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Time Zone" required variant="outlined" />
                )}
                key={(option) => option.value}
              />
            </FormControl>
            <TextField
              id="latitude"
              type="number"
              name="latitude"
              label="Latitude"
              value={v.latitude}
              size="small"
              variant="outlined"
              onChange={(ev) => {
                s.setLatitude(ev.target.value);
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
              error={
                (isNaN(v.latitude) || v.latitude < -90 || v.latitude > 90) && v.latitude?.length > 0
              }
              helperText={
                (isNaN(v.latitude) || v.latitude < -90 || v.latitude > 90) && v.latitude?.length > 0
                  ? "Latitude must be a number between -90 and 90"
                  : ""
              }
            />
            <TextField
              id="longitude"
              type="number"
              name="longitude"
              size="small"
              label="Longitude"
              value={v.longitude}
              variant="outlined"
              onChange={(ev) => {
                s.setLongitude(ev.target.value);
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
              error={
                (isNaN(v.longitude) || v.longitude < -180 || v.longitude > 180) &&
                v.longitude?.length > 0
              }
              helperText={
                (isNaN(v.longitude) || v.longitude < -180 || v.longitude > 180) &&
                v.longitude.length > 0
                  ? "Longitude must be a number between -180 and 180"
                  : ""
              }
            />
          </Box>
          <Box display="flex" mb="2.5rem" alignItems="center">
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <InputLabel size="small" htmlFor="siteState" required>
                Status
              </InputLabel>
              <Select
                id="siteState"
                name="siteState"
                label="Status"
                size="small"
                required
                value={v.siteState}
                variant="outlined"
                onChange={(ev) => {
                  s.setSiteState(ev.target.value);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                }}
              >
                {getStatus()}
              </Select>
            </FormControl>
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Commercial Operat. Date"
                  value={v.commissionedDate}
                  onChange={(newValue) => {
                    s.setCommissionedDate(newValue);
                  }}
                  slots={{
                    openPickerIcon: CalendarMonthOutlinedIcon,
                  }}
                  slotProps={{
                    textField: {
                      InputLabelProps: { shrink: true },
                      size: "small",
                    },
                    openPickerIcon: {
                      fontSize: "20rem",
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  size="large"
                  checked={!!v.siteNotifications}
                  onChange={(ev, checked) => {
                    s.setSiteNotifications(checked);
                  }}
                  disabled={!currentSite?.uuid}
                />
              }
              label="Enable site notifications for Scale users"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "16px",
                  color: "rgba(0, 0, 0, 0.87)",
                },
              }}
            />
          </Box>
          <Typography variant="h6" mb="1.5rem">
            Connections
          </Typography>
          <Box display="flex" mb="3rem" alignItems="flex-start" sx={{ gap: "1.5rem" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              <FormControl sx={{ width: "24.5rem" }}>
                <Autocomplete
                  id="iso"
                  options={l.isoList?.iso || []}
                  getOptionLabel={(option) => option.name}
                  value={l.isoList?.iso?.find((item) => item.uuid === v.iso) || null}
                  size="small"
                  onChange={(event, newValue) => {
                    s.setIso(newValue ? newValue.uuid : "");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="ISO / RTO" variant="outlined" required />
                  )}
                  key={(option) => option.uuid}
                />
              </FormControl>
              <FormControl sx={{ width: "24.5rem" }}>
                <Autocomplete
                  id="utility"
                  options={l.utilityList?.utilities || []}
                  getOptionLabel={(option) => option.name}
                  size="small"
                  value={l.utilityList?.utilities?.find((item) => item.uuid === v.utility) || null}
                  onChange={(event, newValue) => {
                    s.setUtility(newValue ? newValue.uuid : "");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Electric Utility" variant="outlined" required />
                  )}
                  key={(option) => option.uuid}
                />
              </FormControl>
              <FormControl sx={{ width: "24.5rem" }}>
                <Autocomplete
                  id="gasUtility"
                  options={l.gasUtilitiesList || []}
                  loading={l.gasUtilitiesListIsLoading}
                  getOptionLabel={(option) => option.Company}
                  value={l.gasUtilitiesList?.find((item) => item.UUID === v.gasUtility) || null}
                  size="small"
                  onChange={(event, newValue) => {
                    s.setGasUtility(newValue ? newValue.UUID : "");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Gas Utility" variant="outlined" />
                  )}
                  key={(option) => option.UUID}
                />
              </FormControl>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <ConnectionSummaryCard
                  label="Electric Billing Periods"
                  title={calendarId ? (calendarName || calendarDetailData?.results?.[0]?.calendarName) : undefined}
                  periodEndDates={calendarId ? (periodEndDates || undefined) : undefined}
                  fallbackText="Set up a tariff before configuring billing periods."
                />
                <Button
                  variant="text"
                  sx={{
                    alignSelf: "flex-start",
                    fontSize: "14px",
                    lineHeight: "24px",
                    color: "#006681",
                    textTransform: "none",
                    padding: "0",
                    minWidth: "auto",
                    fontWeight: 500,
                    letterSpacing: "0.4px",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                  onClick={onNavigateToIntegrations}
                >
                  Manage Billing Periods
                </Button>
              </Box>
            </Box>
            <Box sx={{ width: "24.5rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {isEmptyTariffState ? (
                <ConnectionSummaryCard
                  label="Electric Tariff"
                  fallbackText="Set up a tariff in Admin settings."
                  showDates={!billConfigsLoading}
                />
              ) : (
                <>
                  <ConnectionSummaryCard
                    label={preConfig && !postConfig ? "Electric Tariff" : "Electric Tariff (Pre-Project)"}
                    title={preConfig?.tariff_name || (preConfig?.master_tariff_id ? `Tariff ID: ${preConfig.master_tariff_id}` : undefined)}
                    subtitle={preConfig?.effective_start_date ? dayjs(preConfig.effective_start_date).format("MMM D, YYYY") : undefined}
                  overrides={preOverridesWithDisplayNames}
                    showDates={!billConfigsLoading}
                    fallbackText="Set up a tariff in Admin settings."
                  />
                  {(!preConfig || postConfig) && (
                    <ConnectionSummaryCard
                      label="Electric Tariff (Post-Project)"
                      title={postConfig?.tariff_name || (postConfig?.master_tariff_id ? `Tariff ID: ${postConfig.master_tariff_id}` : undefined)}
                      subtitle={postConfig?.effective_start_date ? dayjs(postConfig.effective_start_date).format("MMM D, YYYY") : undefined}
                    overrides={postOverridesWithDisplayNames}
                      showDates={!billConfigsLoading}
                      fallbackText="Set up a tariff in Admin settings."
                    />
                  )}
                </>
              )}
              <Button
                variant="text"
                sx={{
                  alignSelf: "flex-start",
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#006681",
                  textTransform: "none",
                  padding: "0",
                  minWidth: "auto",
                  fontWeight: 500,
                  letterSpacing: "0.4px",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
                onClick={onNavigateToIntegrations}
              >
                Manage Tariffs
              </Button>
            </Box>
          </Box>
          <Typography variant="h6" mb="1.5rem">
            Agreements
          </Typography>
          <Box display="flex" mb="1.5rem" alignItems="center">
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <InputLabel size="small" htmlFor="Contract Type">
                Contract Type
              </InputLabel>
              <Select
                id="contractType"
                name="contractType"
                multiple
                label="Contract Type"
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                }}
                size="small"
                value={v.contractType}
                variant="outlined"
                onChange={(ev) => {
                  s.setContractType(ev.target.value);
                }}
              >
                {getContractTypes()}
              </Select>
            </FormControl>
            <FormControl
              sx={{
                width: "11.5rem",
                marginRight: "1.5rem",
              }}
            >
              <InputLabel size="small" htmlFor="Bill Savings Provider">
                Bill Savings Provider
              </InputLabel>
              <Select
                id="billSavingsProvider"
                name="billSavingsProvider"
                label="Bill Savings Provider"
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                }}
                size="small"
                value={v.billSavingsProvider}
                variant="outlined"
                onChange={(ev) => {
                  s.setBillSavingsProvider(ev.target.value);
                }}
              >
                {getbillSavingsProviders()}
              </Select>
            </FormControl>
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Contract End Date"
                  value={v.contractEndDate}
                  onChange={(newValue) => {
                    s.setContractEndDate(newValue);
                  }}
                  slots={{
                    openPickerIcon: CalendarMonthOutlinedIcon,
                  }}
                  slotProps={{
                    textField: {
                      InputLabelProps: { shrink: true },
                      size: "small",
                    },
                    openPickerIcon: {
                      fontSize: "20rem",
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
          </Box>
          <Box display="flex" mb="1.5rem" alignItems="center">
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <InputLabel size="small" htmlFor="Deal Structure">
                Deal Structure
              </InputLabel>
              <Select
                id="dealStructure"
                name="dealStructure"
                label="Deal Structure"
                multiple
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                }}
                size="small"
                value={v.dealStructure}
                variant="outlined"
                onChange={(ev) => {
                  s.setDealStructure(ev.target.value);
                }}
              >
                {getDealStructures()}
              </Select>
            </FormControl>
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <InputLabel size="small" htmlFor="Grid Services Provider">
                Grid Services Provider
              </InputLabel>
              <Select
                id="gridServiceProvider"
                name="gridServiceProvider"
                label="Grid Services Provider"
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 16,
                    fontWeight: 400,
                  },
                }}
                size="small"
                value={v.gridServicesProvider}
                variant="outlined"
                onChange={(ev) => {
                  s.setGridServicesProvider(ev.target.value);
                }}
              >
                {getServicesProviders()}
              </Select>
            </FormControl>
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Acquired Date"
                  value={v.dateAcquired}
                  onChange={(newValue) => {
                    s.setDateAcquired(newValue);
                  }}
                  slots={{
                    openPickerIcon: CalendarMonthOutlinedIcon,
                  }}
                  slotProps={{
                    textField: {
                      InputLabelProps: { shrink: true },
                      size: "small",
                    },
                    openPickerIcon: {
                      fontSize: "20rem",
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
          </Box>
          <Box display="flex" mb="1.5rem" alignItems="center">
            <FormControl sx={{ width: "24.5rem", marginRight: "1.5rem" }}>
              <Autocomplete
                id="sgip"
                name="sgip"
                options={l.sgipOptions}
                getOptionLabel={(option) => option.label}
                value={
                  l.sgipOptions?.find((option) => option.value === v.sgipBudgetCategory) || null
                }
                onChange={(event, newValue) => {
                  s.setSgipBudgetCategory(newValue ? newValue.value : "");
                }}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="SGIP Budget Category" variant="outlined" />
                )}
                key={(option) => option.value}
              />
            </FormControl>
            <FormControl sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="SGIP PBI Start Date"
                  value={v.sgipStartDate}
                  onChange={(newValue) => {
                    s.setSgipStartDate(newValue);
                  }}
                  slots={{
                    openPickerIcon: CalendarMonthOutlinedIcon,
                  }}
                  slotProps={{
                    textField: {
                      InputLabelProps: { shrink: true },
                      size: "small",
                    },
                    openPickerIcon: {
                      fontSize: "20rem",
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
            <TextField
              id="sgipCapacity"
              type="number"
              name="sgipCapacity"
              size="small"
              label="SGIP BESS Capacity (kWh)"
              value={v.sgipCapacity}
              variant="outlined"
              onChange={(ev) => {
                s.setSgipCapacity(ev.target.value);
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
            />
          </Box>
        </Box>
      </FormGroup>

      {isLoading ? <Loading /> : null}
      {error ? <DataComponentError /> : null}
    </Box>
  );
};

SiteInfo.propTypes = {
  styles: PropTypes.object,
  theme: PropTypes.object,
  currentSite: PropTypes.object,
  form: PropTypes.shape({
    values: PropTypes.object,
    set: PropTypes.object,
    helpers: PropTypes.object,
  }),
  lists: PropTypes.object,
  getCountries: PropTypes.func,
  getStatus: PropTypes.func,
  getContractTypes: PropTypes.func,
  getDealStructures: PropTypes.func,
  getbillSavingsProviders: PropTypes.func,
  getServicesProviders: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
  onNavigateToIntegrations: PropTypes.func,
};

export default SiteInfo;
